import OpenAI from 'openai';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import { KimiChatCompletionsModel } from './chat-completions-model';
import {
  getDefaultKimiBaseURL,
  getDefaultKimiKey,
  getDefaultKimiModel,
} from './defaults';

const KIMI_MULTIMODAL_METADATA = {
  imageInput: true,
  imageToolResult: 'synthetic-user',
} as const satisfies NonNullable<ModelMetadata['multimodal']>;

export type KimiModelMetadataResolver =
  | Record<string, ModelMetadata>
  | ((modelName: string) => ModelMetadata | undefined);

export type KimiProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  openAIClient?: OpenAI;
  modelMetadata?: KimiModelMetadataResolver;
};

export class KimiProvider implements ModelProvider {
  #client?: OpenAI;
  #modelCache = new Map<string, Model>();
  #options: KimiProviderOptions;

  constructor(options: KimiProviderOptions = {}) {
    this.#options = options;
    if (this.#options.openAIClient) {
      if (this.#options.apiKey) {
        throw new Error('Cannot provide both apiKey and openAIClient');
      }
      if (this.#options.baseURL) {
        throw new Error('Cannot provide both baseURL and openAIClient');
      }
      this.#client = this.#options.openAIClient;
    }
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultKimiModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return (
      resolveConfiguredModelMetadata(
        this.#options.modelMetadata,
        resolvedModelName,
      ) ?? getKnownKimiModelMetadata(resolvedModelName)
    );
  }

  #getClient(): OpenAI {
    if (!this.#client) {
      this.#client = new OpenAI({
        apiKey: this.#options.apiKey ?? getDefaultKimiKey(),
        baseURL: this.#options.baseURL ?? getDefaultKimiBaseURL(),
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string | undefined): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'kimi_chat_completions']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new KimiChatCompletionsModel(
      this.#getClient(),
      model,
      this.getModelMetadata(model),
    );
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  /**
   * Closes cached model wrappers and clears cache.
   */
  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close KimiProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: KimiModelMetadataResolver | undefined,
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}

function getKnownKimiModelMetadata(
  modelName: string,
): ModelMetadata | undefined {
  const normalized = modelName.trim().toLowerCase();
  if (normalized === 'kimi-k2.6' || normalized === 'kimi-k2.5') {
    return {
      contextWindowTokens: 262_144,
      maxOutputTokens: 32_768,
      multimodal: KIMI_MULTIMODAL_METADATA,
    };
  }
  if (normalized === 'moonshot-v1-8k') {
    return {
      contextWindowTokens: 8_192,
      multimodal: KIMI_MULTIMODAL_METADATA,
    };
  }
  if (normalized === 'moonshot-v1-32k') {
    return {
      contextWindowTokens: 32_768,
      multimodal: KIMI_MULTIMODAL_METADATA,
    };
  }
  if (normalized === 'moonshot-v1-128k') {
    return {
      contextWindowTokens: 131_072,
      multimodal: KIMI_MULTIMODAL_METADATA,
    };
  }
  return undefined;
}
